import { useState } from 'react';

import { dashboardT } from '../../../../../lib/messages';
import { projectReportChart } from '../../../reportExplorer/reportChartModel';
import { ReportMetric } from '../../../reportExplorer/reportEnums';
import { performanceEntityOptions, projectEntityPerformanceChart } from '../../chartModel';
import { PerformanceQueryKind, usePerformanceQuery } from '../../hooks';
import type { PerformanceAnalyzerRange } from '../../model';
import {
  buildStitcherPlacementDetailRequest,
  buildStitcherPlacementsChartRequest,
  buildStitcherPlacementsRequest,
  buildStitcherPublisherRequest,
  buildStitcherPublishersChartRequest,
  buildTopStitcherPublishersRequest,
} from '../../reportRequests';
import { EntityControl } from '../PerformanceControls';
import { PerformanceChartCard } from '../PerformanceChartCard';

export function StitcherPerformance({ range }: { range: PerformanceAnalyzerRange }) {
  const comparisonEnabled = Boolean(range.comparison);
  const [publisherId, setPublisherId] = useState('');
  const [placementId, setPlacementId] = useState('');

  const shortlistRequest = buildTopStitcherPublishersRequest(range.primary);
  const shortlist = usePerformanceQuery(
    PerformanceQueryKind.TopStitcherPublishers,
    shortlistRequest,
  );
  const publishers = performanceEntityOptions(
    shortlist.data,
    'publisherId',
    'publisherName',
    ReportMetric.AdImpressionsStitchedPct,
  );
  const publisherIds = publishers.map(({ id }) => id);
  const topRequest = buildStitcherPublishersChartRequest(range.primary, publisherIds);
  const topComparisonRequest = buildStitcherPublishersChartRequest(
    range.comparison ?? range.primary,
    publisherIds,
  );
  const top = usePerformanceQuery(
    PerformanceQueryKind.Standard,
    topRequest,
    publisherIds.length > 0,
  );
  const topComparison = usePerformanceQuery(
    PerformanceQueryKind.Standard,
    topComparisonRequest,
    Boolean(range.comparison) && publisherIds.length > 0,
  );

  const publisherRequest = buildStitcherPublisherRequest(range.primary, publisherId);
  const publisherComparisonRequest = buildStitcherPublisherRequest(
    range.comparison ?? range.primary,
    publisherId,
  );
  const publisher = usePerformanceQuery(
    PerformanceQueryKind.Standard,
    publisherRequest,
    Boolean(publisherId),
  );
  const publisherComparison = usePerformanceQuery(
    PerformanceQueryKind.Standard,
    publisherComparisonRequest,
    Boolean(range.comparison) && Boolean(publisherId),
  );

  const placementShortlistRequest = buildStitcherPlacementsRequest(range.primary, publisherId);
  const placementShortlist = usePerformanceQuery(
    PerformanceQueryKind.StitcherPlacements,
    placementShortlistRequest,
    Boolean(publisherId),
  );
  const placementOptions = performanceEntityOptions(
    placementShortlist.data,
    'placementId',
    'placement',
    ReportMetric.AdImpressionsStitchedPct,
    Number.MAX_SAFE_INTEGER,
  );
  const placementIds = placementOptions.map(({ id }) => id);
  const placementsRequest = buildStitcherPlacementsChartRequest(
    range.primary,
    publisherId,
    placementIds,
  );
  const placementsComparisonRequest = buildStitcherPlacementsChartRequest(
    range.comparison ?? range.primary,
    publisherId,
    placementIds,
  );
  const placements = usePerformanceQuery(
    PerformanceQueryKind.Standard,
    placementsRequest,
    placementIds.length > 0,
  );
  const placementsComparison = usePerformanceQuery(
    PerformanceQueryKind.Standard,
    placementsComparisonRequest,
    Boolean(range.comparison) && placementIds.length > 0,
  );

  const placementRequest = buildStitcherPlacementDetailRequest(
    range.primary,
    publisherId,
    placementId,
  );
  const placementComparisonRequest = buildStitcherPlacementDetailRequest(
    range.comparison ?? range.primary,
    publisherId,
    placementId,
  );
  const placement = usePerformanceQuery(
    PerformanceQueryKind.Standard,
    placementRequest,
    Boolean(placementId),
  );
  const placementComparison = usePerformanceQuery(
    PerformanceQueryKind.Standard,
    placementComparisonRequest,
    Boolean(range.comparison) && Boolean(placementId),
  );

  const topProjection = projectEntityPerformanceChart(
    top.data,
    range.comparison ? topComparison.data : undefined,
    publishers,
    {
      identityField: 'publisherId',
      metrics: [ReportMetric.AdImpressionsStitchedPct],
    },
  );
  const publisherProjection = projectReportChart(
    publisherRequest,
    publisher.data,
    range.comparison ? { response: publisherComparison.data, days: 1 } : undefined,
  );
  const placementsProjection = projectEntityPerformanceChart(
    placements.data,
    range.comparison ? placementsComparison.data : undefined,
    placementOptions,
    {
      identityField: 'placementId',
      metrics: [ReportMetric.AdImpressionsStitchedPct],
    },
  );
  const placementProjection = projectReportChart(
    placementRequest,
    placement.data,
    range.comparison ? { response: placementComparison.data, days: 1 } : undefined,
  );

  return (
    <div className="space-y-4">
      <PerformanceChartCard
        title={dashboardT('performanceAnalyzer.stitcherTopPublishers')}
        {...topProjection}
        loading={
          shortlist.isLoading || top.isLoading || (comparisonEnabled && topComparison.isLoading)
        }
        error={shortlist.isError || top.isError || (comparisonEnabled && topComparison.isError)}
        action={
          <EntityControl
            label={dashboardT('performanceAnalyzer.selectPublisher')}
            options={publishers}
            value={publisherId}
            onChange={(value) => {
              setPublisherId(value);
              setPlacementId('');
            }}
          />
        }
      />
      {publisherId ? (
        <>
          <PerformanceChartCard
            title={dashboardT('performanceAnalyzer.publisherDetails')}
            {...publisherProjection}
            loading={publisher.isLoading || (comparisonEnabled && publisherComparison.isLoading)}
            error={publisher.isError || (comparisonEnabled && publisherComparison.isError)}
          />
          <PerformanceChartCard
            title={dashboardT('performanceAnalyzer.publisherPlacements')}
            {...placementsProjection}
            loading={
              placementShortlist.isLoading ||
              placements.isLoading ||
              (comparisonEnabled && placementsComparison.isLoading)
            }
            error={
              placementShortlist.isError ||
              placements.isError ||
              (comparisonEnabled && placementsComparison.isError)
            }
            action={
              <EntityControl
                label={dashboardT('performanceAnalyzer.selectPlacement')}
                options={placementOptions}
                value={placementId}
                onChange={setPlacementId}
              />
            }
          />
        </>
      ) : null}
      {placementId ? (
        <PerformanceChartCard
          title={dashboardT('performanceAnalyzer.placementDetails')}
          {...placementProjection}
          loading={placement.isLoading || (comparisonEnabled && placementComparison.isLoading)}
          error={placement.isError || (comparisonEnabled && placementComparison.isError)}
        />
      ) : null}
    </div>
  );
}
